import React from "react";
import { Dialog, Link } from "@sscs/ui";
import { humanReadableBytes } from "@sscs/utils";
import { FILE_NAME_CHARACTERS_LIMIT } from "../../constants";

type UploadIssuesProps = {
  uploadedFile: File;
  hasInsufficientCapacity: boolean;
  remainingUsage: number;
  fileHasUnsupportedExtension: boolean;
  fileHasLimitedSupport: boolean;
  isOverSizeLimit: boolean;
  isFileNameTooLong: boolean;
  uploadLimit: number;
};

const UploadIssues = ({
  uploadedFile,
  hasInsufficientCapacity,
  remainingUsage,
  fileHasUnsupportedExtension,
  fileHasLimitedSupport,
  isOverSizeLimit,
  isFileNameTooLong,
  uploadLimit,
}: UploadIssuesProps): React.JSX.Element | null => {
  if (isOverSizeLimit) {
    return (
      <Dialog.Banner type="error" title="Size limit exceeded">
        Selected file (
        <strong>{humanReadableBytes(uploadedFile.size, 2)}</strong>) exceeds
        upload limit (<strong>{humanReadableBytes(uploadLimit, 2)}</strong>
        ).
      </Dialog.Banner>
    );
  }

  if (isFileNameTooLong) {
    return (
      <Dialog.Banner type="error" title="File name too long">
        Selected file name (
        <strong>{uploadedFile.name.length.toLocaleString()} characters</strong>)
        exceeds maximum length (
        <strong>{FILE_NAME_CHARACTERS_LIMIT} characters</strong>
        ).
        <br />
        Please rename the file.
      </Dialog.Banner>
    );
  }

  if (hasInsufficientCapacity) {
    return (
      <Dialog.Banner type="error" title="Insufficient capacity">
        Selected file (
        <strong>{humanReadableBytes(uploadedFile.size, 2)}</strong>) is larger
        than remaining analysis capacity (
        <strong>{humanReadableBytes(remainingUsage, 2)}</strong>)
      </Dialog.Banner>
    );
  }

  if (fileHasUnsupportedExtension) {
    return (
      <Dialog.Banner
        type="warning"
        title="Warning: The selected file format is not supported"
      >
        Partial analysis report will be generated. Consult{" "}
        <Link
          id="usupported-docs-link"
          href="https://docs.secure.software/concepts/filetypes#unsupported"
          title="unsupported formats list"
          target="_blank"
          rel="noreferrer"
          inline
        >
          the documentation
        </Link>{" "}
        for details.
      </Dialog.Banner>
    );
  }

  if (fileHasLimitedSupport) {
    return (
      <Dialog.Banner
        type="warning"
        title="Warning: The selected file format has limited support"
      >
        Virtual machines with MBR/GPT/LVM/EFI partitioning and
        NTFS/FAT/EXT/XFS/BTRFS/UFS2/ZFS file systems only.
      </Dialog.Banner>
    );
  }

  return null;
};

export default UploadIssues;
